import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { IoMdClose } from "react-icons/io";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { MdMoreVert, MdEdit, MdDeleteOutline, MdToken, MdUpgrade } from "react-icons/md";
import DeleteModal from "../../components/DeleteModal";
import UserService from "../../services/user";
import { toastError, toastSuccess } from "../../components/ToastNotification";
import TrainingService from "../../services/training-group";
import Loader from "../../assets/images/icon/LinkoSpinner.gif";
import { CustomPagination } from "../../components/Pagination";
import CustomDropdown from "../../components/CustomDropdown";
import Select from "react-select";
import { setInitial } from "../../store/slices/chatSlice";
import { useDispatch } from "react-redux";
import { useLang } from "../../context/LanguageContext";
import TokenModal from "./TokenModal";
import UpgradePlanModal from "./UpgradePlanModal";

const TRANSLATIONS = {
  EN: {
    title: "Users",
    subtitle: "Manage all admin users",
    addUser: "Add User",
    colId: "ID",
    colName: "Name",
    colEmail: "Email",
    colTeam: "Team",
    colSubscription: "Subscription",
    colTrial: "Trial",
    colTrialExpiry: "Trial Expiry",
    colSubExpiry: "Sub. Expiry",
    colUsedTokens: "Used Tokens",
    colTotalTokens: "Total Tokens",
    colActions: "Actions",
    edit: "Edit",
    delete: "Delete",
    token: "Token",
    upgradePlan: "Upgrade Plan",
    noUsers: "No users found",
    noUsersHint: "Add your first user to get started",
    modalAddTitle: "Add User",
    modalEditTitle: "Update User",
    labelFullName: "Full Name",
    labelEmail: "Email",
    labelPassword: "Password",
    labelTeamGroup: "Team / Group",
    placeholderName: "Enter full name",
    placeholderEmail: "Enter email",
    placeholderPassword: "Enter password",
    placeholderGroup: "Select or type a group",
    cancel: "Cancel",
    submit: "Submit",
    update: "Update",
    errName: "Full name is required",
    errEmail: "Email is required",
    errEmailFormat: "Invalid email format",
    errPassword: "Password is required",
    errPasswordMin: "Password must be at least 8 characters.",
    errGroup: "Group is required",
    toastAdded: "User added successfully!",
    toastUpdated: "User updated successfully!",
    toastDeleted: "User deleted successfully!",
    toastGroupChanged: "Group changed successfully!",
    toastTokenUpdated: "Token updated successfully!",
    updateToken: "Update Token",
    labelToken: "Token",
    labelEndDate: "End Date",
    placeholderToken: "Enter token amount",
    searchPlaceholder: "Search by name or email...",
    filterByGroup: "Filter by group",
    allGroups: "All Groups",
    filterByPlan: "Filter by plan",
    allPlans: "All Plans",
    noPlan: "No Plan",
    results: "results",
    clearAll: "Clear all",
  },
  ES: {
    title: "Usuarios",
    subtitle: "Gestionar todos los usuarios administradores",
    addUser: "Agregar Usuario",
    colId: "ID",
    colName: "Nombre",
    colEmail: "Correo",
    colTeam: "Equipo",
    colSubscription: "Suscripción",
    colTrial: "Prueba",
    colTrialExpiry: "Vence Prueba",
    colSubExpiry: "Vence Susc.",
    colUsedTokens: "Tokens Usados",
    colTotalTokens: "Tokens Totales",
    colActions: "Acciones",
    edit: "Editar",
    delete: "Eliminar",
    token: "Token",
    upgradePlan: "Actualizar Plan",
    noUsers: "No se encontraron usuarios",
    noUsersHint: "Agrega tu primer usuario para comenzar",
    modalAddTitle: "Agregar Usuario",
    modalEditTitle: "Actualizar Usuario",
    labelFullName: "Nombre Completo",
    labelEmail: "Correo Electrónico",
    labelPassword: "Contraseña",
    labelTeamGroup: "Equipo / Grupo",
    placeholderName: "Ingresa el nombre completo",
    placeholderEmail: "Ingresa el correo",
    placeholderPassword: "Ingresa la contraseña",
    placeholderGroup: "Selecciona o escribe un grupo",
    cancel: "Cancelar",
    submit: "Enviar",
    update: "Actualizar",
    errName: "Se requiere nombre completo",
    errEmail: "Se requiere correo electrónico",
    errEmailFormat: "Formato de correo electrónico no válido",
    errPassword: "Se requiere contraseña",
    errPasswordMin: "La contraseña debe tener al menos 8 caracteres.",
    errGroup: "Se requiere grupo",
    toastAdded: "¡Usuario agregado exitosamente!",
    toastUpdated: "Usuario actualizado exitosamente!",
    toastDeleted: "¡Usuario eliminado exitosamente!",
    toastGroupChanged: "¡El grupo cambió exitosamente!",
    toastTokenUpdated: "¡Token actualizado exitosamente!",
    updateToken: "Actualizar Token",
    labelToken: "Token",
    labelEndDate: "Fecha de Fin",
    placeholderToken: "Ingresa la cantidad de tokens",
    searchPlaceholder: "Buscar por nombre o correo...",
    filterByGroup: "Filtrar por grupo",
    allGroups: "Todos los Grupos",
    filterByPlan: "Filtrar por plan",
    allPlans: "Todos los Planes",
    noPlan: "Sin Plan",
    results: "resultados",
    clearAll: "Limpiar",
  },
};

const formatTokens = (num) => {
  if (num == null) return "—";
  if (num >= 1000000) return (num / 1000000).toFixed(1).replace(/\.0$/, "") + "M";
  if (num >= 1000) return (num / 1000).toFixed(1).replace(/\.0$/, "") + "K";
  return num.toString();
};

const UserAdmin = () => {
  const dispatch = useDispatch();
  const { lang } = useLang();
  const t = TRANSLATIONS[lang];
  const [loading, setLoading] = useState(true);
  const [paginationLoading, setPaginationLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [groupId, setGroupId] = useState("");
  const [groupInput, setGroupInput] = useState("");
  const [customGroups, setCustomGroups] = useState([]);
  const [isSubmiting, setSubmiting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [userId, setUserId] = useState("");
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const parentDropdownRef = useRef(null);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const PAGE_SIZES = [10, 20, 30, 50, 100];
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [isDropdownLoading, setIsDropdownLoading] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [tokenModal, setTokenModal] = useState(false);
  const [selectedTokenUser, setSelectedTokenUser] = useState(null);
  const [planModal, setPlanModal] = useState(false);
  const [selectedPlanUser, setSelectedPlanUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const debounceTimer = useRef(null);
  const [groupFilter, setGroupFilter] = useState("");
  const [planFilter, setPlanFilter] = useState("");
  const [openActionMenuId, setOpenActionMenuId] = useState(null);
  const [actionMenuPos, setActionMenuPos] = useState({ top: 0, bottom: "auto", right: 0 });

  const handleGroupSelect = (selectedOption) => {
    setGroupId(selectedOption?.value);
    setSelectedGroup(selectedOption);
    setGroupInput(selectedOption.groupName);
    formik.setFieldValue("group", selectedOption ? selectedOption.value : "");
  };

  const handleGroupInputChange = (inputValue) => {
    setGroupInput(inputValue);
  };

  const groupOptions = [
    ...(Array.isArray(groups) ? groups : []).map((group) => ({
      value: group._id,
      label: group.groupName,
    })),
  ];

  const handleChange = (type, page) => {
    const total = totalCount / pageSize;
    const newPage = Math.ceil(total);
    if (type === "Next") {
      if (page !== newPage) setPage(page + 1);
    } else if (type === "Previous") {
      if (page !== 1) setPage(page - 1);
    } else if (type === "First") {
      if (page !== 1) setPage(1);
    } else if (type === "Last") {
      if (page !== newPage) setPage(newPage);
    } else {
      setPage(page);
    }
  };

  const handleDataPerPage = (e) => {
    setPageSize(Number(e.target.value));
    setPage(1);
  };

  const openDeleteModal = (user) => {
    setUserToDelete(user);
    setIsDeleteModalOpen(true);
    setUserId(user?._id);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setUserToDelete(null);
    setUserId("");
  };

  const toggleActionMenu = (event, item) => {
    if (openActionMenuId === item._id) {
      setOpenActionMenuId(null);
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const menuHeight = 190;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUpward = spaceBelow < menuHeight && rect.top > spaceBelow;

    setActionMenuPos({
      top: openUpward ? "auto" : rect.bottom + 6,
      bottom: openUpward ? window.innerHeight - rect.top + 6 : "auto",
      right: window.innerWidth - rect.right,
    });
    setOpenActionMenuId(item._id);
  };

  const closeActionMenu = () => setOpenActionMenuId(null);

  const handleUpdate = (item) => {
    setGroupId("");
    setIsModalOpen(true);
    setIsEditing(true);
    setEditingUserId(item._id);
    formik.setValues({
      name: !item.firstName || item.firstName === "undefined" ? "" : item.firstName,
      email: item.email || "",
      password: "",
      group: "",
    });
    const previousGroup = groupOptions.find((group) => group.value === item?.trainingGroupId?._id);
    setSelectedGroup(previousGroup || null);
    setGroupInput(previousGroup?.label || "");
  };

  const handleDelete = async (closeModal) => {
    setDeleting(true);
    try {
      const response = await UserService?.deleteUser(userId);
      if (response) {
        toastSuccess(t.toastDeleted);
        setUsers((prevUsers) =>
          prevUsers.filter((user) => user.email !== response?.data?.data?.email)
        );
      }
    } catch (error) {
      toastError(error?.response?.data?.message);
    } finally {
      setIsDeleteModalOpen(false);
      setUserToDelete(null);
      setUserId("");
      closeModal();
      setDeleting(false);
    }
  };

  const defaultGroups = ["User Group A", "User Group B", "User Group C"];

  const getOrderedGroups = () => {
    const userGroups = [...new Set(users.map((user) => user.group))];
    const allGroups = [
      ...new Set([...customGroups, ...userGroups, ...defaultGroups]),
    ];
    return allGroups;
  };

  const initialValues = useMemo(() => {
    if (isEditing && editingUserId) {
      const user = users.find((u) => u._id === editingUserId);
      if (user) {
        return {
          name: !user.firstName || user.firstName === "undefined" ? "" : user.firstName,
          email: user.email || "",
          password: "",
          group: "",
        };
      }
    }
    return { name: "", email: "", password: "", group: "" };
  }, [isEditing, editingUserId, users]);

  const validationSchema = Yup.object({
    name: Yup.string().required(t.errName),
    email: isEditing
      ? Yup.string()
      : Yup.string().email(t.errEmailFormat).required(t.errEmail),
    password: isEditing
      ? Yup.string()
      : Yup.string().min(8, t.errPasswordMin).required(t.errPassword),
    group: Yup.string(),
  });

  const handleSubmit = async (values) => {
    const { name, email, password } = values;
    const formData = new FormData();
    formData.append("firstName", name);
    if (groupId) formData.append("trainingGroupId", groupId);
    if (!isEditing) {
      formData.append("email", email);
      formData.append("password", password);
    }
    if (!isEditing) {
      try {
        setSubmiting(true);
        const response = await UserService?.createUser(formData);
        if (response) {
          toastSuccess(t.toastAdded);
          getAllUser();
        }
      } catch (error) {
        toastError(error?.response?.data?.message);
      } finally {
        formik.resetForm();
        setSubmiting(false);
        setSelectedGroup("");
        setGroupInput("");
        setIsModalOpen(false);
        setIsEditing(false);
        setEditingUserId("");
        setGroupId("");
      }
    } else {
      try {
        setSubmiting(true);
        const response = await UserService?.updateUser(editingUserId, formData);
        if (response) {
          toastSuccess(t.toastUpdated);
          setUsers((prevUsers) =>
            prevUsers.map((user) =>
              user.email === response?.data?.data?.email
                ? {
                  ...user,
                  firstName: response?.data?.data?.firstName,
                  trainingGroupId: {
                    ...user.trainingGroupId,
                    groupName: response?.data?.data?.trainingGroupId?.groupName,
                  },
                }
                : user
            )
          );
        }
      } catch (error) {
        toastError(error?.response?.data?.message);
      } finally {
        formik.resetForm();
        setSubmiting(false);
        setSelectedGroup("");
        setGroupInput("");
        setIsModalOpen(false);
        setIsEditing(false);
        setEditingUserId("");
      }
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
    enableReinitialize: true,
  });

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && groupInput && !getOrderedGroups().includes(groupInput)) {
      e.preventDefault();
      setCustomGroups((prev) => [groupInput, ...prev]);
      setSelectedGroup(groupInput);
      setIsOpen(false);
    }
  };

  const closeModal = () => {
    setSelectedGroup("");
    setIsModalOpen(false);
    setIsEditing(false);
    setEditingUserId("");
    setGroupId("");
    setGroupInput("");
    setShowPassword(false);
    formik.resetForm();
  };

  const getAllGroups = async () => {
    try {
      const response = await TrainingService?.getAllGroup();
      if (response) {
        const data = response?.data?.data?.groups || response?.data?.data;
        setGroups(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      toastError(error?.response?.data?.message);
    }
  };

  const getAllUser = async () => {
    if (!users || users.length === 0) setLoading(true);
    setPaginationLoading(true);
    try {
      const payload = { page, limit: pageSize };
      if (groupFilter) payload.groupName = groupFilter;
      if (planFilter) payload.plan_name = planFilter;
      if (debouncedSearch) payload.search = debouncedSearch;
      const response = await UserService?.getAllUser(payload);
      if (response) {
        setUsers(Array.isArray(response?.data?.data) ? response?.data?.data : []);
        setTotalCount(response?.data?.totalCount);
      }
    } catch (error) {
      toastError(error?.response?.data?.message);
    } finally {
      setLoading(false);
      setPaginationLoading(false);
    }
  };

  const handleGroupChange = async (item, newGroupName) => {
    if (!item?._id) return;
    setIsDropdownLoading(item._id);
    const formData = new FormData();
    formData.append("trainingGroupId", newGroupName);
    try {
      const response = await UserService?.updateUser(item?._id, formData);
      if (response) {
        toastSuccess(t.toastGroupChanged);
        const matchingGroup = (Array.isArray(groups) ? groups : []).find(
          (g) => g._id === newGroupName || g.groupName === newGroupName
        );
        const updatedGroupName =
          matchingGroup?.groupName ||
          response?.data?.data?.trainingGroupId?.groupName ||
          (typeof response?.data?.data?.trainingGroupId === "string"
            ? response?.data?.data?.trainingGroupId
            : "");

        setUsers((prevUsers) =>
          prevUsers.map((user) =>
            user._id === item._id || (user.email && user.email === item.email)
              ? {
                ...user,
                trainingGroupId: {
                  ...(typeof user.trainingGroupId === "object" ? user.trainingGroupId : {}),
                  _id: newGroupName,
                  groupName: updatedGroupName,
                },
              }
              : user
          )
        );
      }
    } catch (error) {
      toastError(error?.response?.data?.message || "Failed to update group");
    } finally {
      setIsDropdownLoading("");
    }
  };

  const openTokenModal = (user) => {
    setSelectedTokenUser(user);
    setTokenModal(true);
  };

  const closeTokenModal = () => {
    setTokenModal(false);
    setSelectedTokenUser(null);
  };

  const openPlanModal = (user) => {
    setSelectedPlanUser(user);
    setPlanModal(true);
  };

  const closePlanModal = () => {
    setPlanModal(false);
    setSelectedPlanUser(null);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, 400);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setDebouncedSearch("");
    setPage(1);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
  };

  useEffect(() => {
    getAllUser();
  }, [page, pageSize, groupFilter, planFilter, debouncedSearch]);

  useEffect(() => {
    getAllGroups();
  }, []);

  useEffect(() => {
    dispatch(setInitial(true));
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleClickOutsideActionMenu = (event) => {
      if (event.target.closest?.("[data-action-menu]")) return;
      setOpenActionMenuId(null);
    };
    const handleScrollClose = () => setOpenActionMenuId(null);
    document.addEventListener("mousedown", handleClickOutsideActionMenu);
    document.addEventListener("scroll", handleScrollClose, true);
    return () => {
      document.removeEventListener("mousedown", handleClickOutsideActionMenu);
      document.removeEventListener("scroll", handleScrollClose, true);
    };
  }, []);

  return (
    <div className="flex flex-col w-full h-full p-4 lg:p-6 overflow-hidden">

      {/* Page header */}
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <div>
          <h1 className="font-roboto font-bold text-xl lg:text-2xl text-primary uppercase tracking-wide">
            {t.title}
          </h1>
          <p className="font-roboto text-sm text-gray-400 mt-0.5">
            {t.subtitle}
          </p>
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-primary text-white font-roboto font-semibold text-sm rounded-xl hover:bg-primary/90 transition-all uppercase tracking-wide shadow-sm"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          <span className="hidden sm:inline">{t.addUser}</span>
        </button>
      </div>

      {/* Search + Filter Toolbar */}
      <div className="bg-white rounded-2xl border border-primary/10 shadow-sm px-4 py-3 mb-4 flex-shrink-0">
        <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-3">

          {/* Search Input */}
          <div className="relative flex-1 min-w-0">
            <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder={t.searchPlaceholder}
              className="w-full pl-10 pr-10 py-2.5 rounded-xl border border-gray-200 bg-gray-50/80 font-roboto text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/30 focus:bg-white transition-all"
            />
            {searchTerm && (
              <button
                onClick={handleClearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <IoMdClose className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Divider */}
          <div className="hidden lg:block w-px h-8 bg-gray-200" />

          {/* Group Filter */}
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-52 flex-shrink-0">
              <Select
                value={groupFilter ? { value: groupFilter, label: groupFilter } : { value: "", label: t.allGroups }}
                onChange={(option) => { setGroupFilter(option?.value || ""); setPage(1); }}
                options={[
                  { value: "", label: t.allGroups },
                  ...((Array.isArray(groups) ? groups : []).map((group) => ({ value: group.groupName, label: group.groupName }))),
                ]}
                isSearchable
                menuPlacement="auto"
                menuPosition="fixed"
                menuPortalTarget={document.body}
                placeholder={t.filterByGroup}
                className="capitalize text-sm"
                classNamePrefix="react-select"
                noOptionsMessage={() => lang === "ES" ? "Sin grupos" : "No groups"}
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  control: (base) => ({ ...base, minHeight: "38px", borderColor: "transparent", boxShadow: "none", backgroundColor: "rgb(249 250 251 / 0.8)" }),
                  singleValue: (base) => ({ ...base, color: "#374151", fontWeight: 500 }),
                }}
              />
            </div>
          </div>

          {/* Divider */}
          <div className="hidden lg:block w-px h-8 bg-gray-200" />

          {/* Plan Filter */}
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-48 flex-shrink-0">
              <Select
                value={
                  planFilter
                    ? {
                        value: planFilter,
                        label:
                          planFilter === "null"
                            ? t.noPlan
                            : planFilter.toLowerCase() === "adminprovide"
                            ? "Organizations"
                            : planFilter,
                      }
                    : { value: "", label: t.allPlans }
                }
                onChange={(option) => { setPlanFilter(option?.value || ""); setPage(1); }}
                options={[
                  { value: "", label: t.allPlans },
                  { value: "AdminProvide", label: "Organizations" },
                  { value: "Professionals", label: "Professionals" },
                  { value: "null", label: t.noPlan },
                ]}
                isSearchable={false}
                menuPlacement="auto"
                menuPosition="fixed"
                menuPortalTarget={document.body}
                placeholder={t.filterByPlan}
                className="text-sm"
                classNamePrefix="react-select"
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  control: (base) => ({ ...base, minHeight: "38px", borderColor: "transparent", boxShadow: "none", backgroundColor: "rgb(249 250 251 / 0.8)" }),
                  singleValue: (base) => ({ ...base, color: "#374151", fontWeight: 500 }),
                }}
              />
            </div>
          </div>

          {/* Divider */}
          <div className="hidden lg:block w-px h-8 bg-gray-200" />

          {/* Result count */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* <span className="font-roboto text-xs text-gray-400 whitespace-nowrap">
              <span className="font-semibold text-gray-600">{totalCount}</span> {t.results}
            </span> */}
            {(debouncedSearch || groupFilter || planFilter) && (
              <button
                onClick={() => { handleClearSearch(); setGroupFilter(""); setPlanFilter(""); setPage(1); }}
                className="text-xs font-roboto font-semibold text-primary/70 hover:text-primary transition-colors whitespace-nowrap"
              >
                {t.clearAll}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 flex flex-col">
        {!loading && (!users || users.length === 0) ? (
          <div className="flex flex-col items-center justify-center flex-1 gap-3">
            <div className="h-20 w-20 rounded-full bg-[#F0EEF8] flex items-center justify-center">
              <svg className="h-10 w-10 text-primary/30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <p className="font-roboto font-semibold text-primary text-lg">{t.noUsers}</p>
            <p className="font-roboto text-sm text-primary/60">{t.noUsersHint}</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-primary/10 shadow-sm flex flex-col flex-1 min-h-0 overflow-hidden">
            <div className="flex-1 min-h-0 overflow-auto">
              <table className="w-full text-sm min-w-[1100px]">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-primary text-white [&>th:first-child]:rounded-tl-2xl [&>th:last-child]:rounded-tr-2xl">
                    <th className="text-left px-4 py-3.5 font-roboto font-semibold uppercase tracking-wider text-xs w-10">{t.colId}</th>
                    <th className="text-left px-4 py-3.5 font-roboto font-semibold uppercase tracking-wider text-xs">{t.colName}</th>
                    <th className="text-left px-4 py-3.5 font-roboto font-semibold uppercase tracking-wider text-xs">{t.colTeam}</th>
                    <th className="text-center px-4 py-3.5 font-roboto font-semibold uppercase tracking-wider text-xs whitespace-nowrap">{t.colSubscription}</th>
                    <th className="text-left px-4 py-3.5 font-roboto font-semibold uppercase tracking-wider text-xs whitespace-nowrap">{t.colTrialExpiry}</th>
                    <th className="text-left px-4 py-3.5 font-roboto font-semibold uppercase tracking-wider text-xs whitespace-nowrap">{t.colSubExpiry}</th>
                    <th className="text-right px-4 py-3.5 font-roboto font-semibold uppercase tracking-wider text-xs whitespace-nowrap">{t.colUsedTokens}</th>
                    <th className="text-right px-4 py-3.5 font-roboto font-semibold uppercase tracking-wider text-xs whitespace-nowrap">{t.colTotalTokens}</th>
                    <th className="text-center px-4 py-3.5 font-roboto font-semibold uppercase tracking-wider text-xs">{t.colActions}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-primary/8 bg-white">
                  {loading || paginationLoading ? (
                    <UserTableSkeletonRows pageSize={pageSize} />
                  ) : (
                    users.map((item, index) => (
                      <tr key={index} className="hover:bg-[#F0EEF8]/50 transition-colors">
                        <td className="px-4 py-3 text-gray-400 font-roboto text-sm">
                          {(page - 1) * pageSize + index + 1}
                        </td>
                        <td className="px-4 py-3 font-roboto">
                          <div className="font-semibold text-gray-800 leading-tight">
                            {item.firstName && item.firstName !== "undefined"
                              ? item.firstName
                              : item.email.split("@")[0]}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">{item.email}</div>
                        </td>
                        <td className="px-4 py-3">
                          <CustomDropdown
                            item={item}
                            groups={groups}
                            handleGroupChange={handleGroupChange}
                            isDropdownLoading={isDropdownLoading}
                            setIsDropdownLoading={setIsDropdownLoading}
                            lang={lang}
                          />
                        </td>
                        <td className="px-4 py-3 text-center whitespace-nowrap">
                          {(() => {
                            const isTrialActive = item.is_free_triee_use &&
                              item.free_trial_expire_date &&
                              new Date(item.free_trial_expire_date) > new Date();

                            if (isTrialActive) {
                              return (
                                <span className="px-2.5 py-1 rounded-full font-roboto text-xs font-bold uppercase tracking-wide bg-blue-50 text-blue-700 border border-blue-200">
                                  {lang === "ES" ? "Prueba Gratis" : "Free Trial"}
                                </span>
                              );
                            } else if (item.plan_name || item.is_subscription) {
                              const displayPlan =
                                item.plan_name?.toLowerCase() === "adminprovide"
                                  ? "ORGANIZATIONS"
                                  : item.plan_name || (lang === "ES" ? "Suscripción" : "Subscription");
                              return (
                                <span className="px-2.5 py-1 rounded-full font-roboto text-xs font-bold uppercase tracking-wide bg-green-50 text-green-700 border border-green-200">
                                  {displayPlan}
                                </span>
                              );
                            } else {
                              return (
                                <span className="px-2.5 py-1 rounded-full font-roboto text-xs font-bold uppercase tracking-wide bg-red-50 text-red-500 border border-red-200">
                                  {lang === "ES" ? "Ninguna" : "None"}
                                </span>
                              );
                            }
                          })()}
                        </td>
                        <td className="px-4 py-3 font-roboto text-xs text-gray-500 whitespace-nowrap">
                          {(() => {
                            const expiryDate = item.free_trial_expire_date;
                            if (!expiryDate) return "—";
                            return new Date(expiryDate) < new Date()
                              ? <span className="text-red-500 font-semibold">{lang === "ES" ? "Vencido" : "Expired"}</span>
                              : new Date(expiryDate).toLocaleDateString();
                          })()}
                        </td>
                        <td className="px-4 py-3 font-roboto text-xs text-gray-500 whitespace-nowrap">
                          {(() => {
                            const expiryDate = item.subscription_expiresAt;
                            if (!expiryDate) return "—";
                            return new Date(expiryDate) < new Date()
                              ? <span className="text-red-500 font-semibold">{lang === "ES" ? "Vencido" : "Expired"}</span>
                              : new Date(expiryDate).toLocaleDateString();
                          })()}
                        </td>
                        <td className="px-4 py-3 font-roboto text-sm font-semibold text-primary text-right whitespace-nowrap">
                          {formatTokens(item.used_tokens)}
                        </td>
                        <td className="px-4 py-3 font-roboto text-sm font-semibold text-primary text-right whitespace-nowrap">
                          {formatTokens(item.total_tokens)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center" data-action-menu>
                            <button
                              type="button"
                              onClick={(e) => toggleActionMenu(e, item)}
                              className={`h-8 w-8 flex items-center justify-center rounded-full transition-colors ${
                                openActionMenuId === item._id
                                  ? "bg-[#F0EEF8] text-primary"
                                  : "text-gray-400 hover:bg-[#F0EEF8] hover:text-primary"
                              }`}
                            >
                              <MdMoreVert className="h-5 w-5" />
                            </button>

                            {openActionMenuId === item._id && (
                              <div
                                className="fixed z-50 w-44 bg-white rounded-xl shadow-lg border border-primary/10 py-1.5"
                                style={{ top: actionMenuPos.top, bottom: actionMenuPos.bottom, right: actionMenuPos.right }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <button
                                  type="button"
                                  onClick={() => { closeActionMenu(); handleUpdate(item); }}
                                  className="w-full flex items-center gap-2.5 px-4 py-2 font-roboto text-sm font-medium text-gray-700 hover:bg-[#F0EEF8] hover:text-primary transition-colors"
                                >
                                  <MdEdit className="h-4 w-4" />
                                  {t.edit}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => { closeActionMenu(); openDeleteModal(item); }}
                                  className="w-full flex items-center gap-2.5 px-4 py-2 font-roboto text-sm font-medium text-red-500 hover:bg-red-50 transition-colors"
                                >
                                  <MdDeleteOutline className="h-4 w-4" />
                                  {t.delete}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => { closeActionMenu(); openTokenModal(item); }}
                                  className="w-full flex items-center gap-2.5 px-4 py-2 font-roboto text-sm font-medium text-gray-700 hover:bg-[#F0EEF8] hover:text-primary transition-colors"
                                >
                                  <MdToken className="h-4 w-4" />
                                  {t.token}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => { closeActionMenu(); openPlanModal(item); }}
                                  className="w-full flex items-center gap-2.5 px-4 py-2 font-roboto text-sm font-medium text-gray-700 hover:bg-[#F0EEF8] hover:text-primary transition-colors"
                                >
                                  <MdUpgrade className="h-4 w-4" />
                                  {t.upgradePlan}
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex-shrink-0 border-t border-primary/10">
              <CustomPagination
                total={totalCount}
                page={page}
                handleChange={handleChange}
                dataPerPage={pageSize}
                handleDataPerPage={handleDataPerPage}
                PAGE_SIZES={PAGE_SIZES}
                lang={lang}
              />
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteModal
        handleDelete={handleDelete}
        content={`${userToDelete?.firstName ?? ""} ${userToDelete?.lastName ?? ""}`.trim() || userToDelete?.email || "this user"}
        deleting={deleting}
        lang={lang}
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteModal}
      />

      {/* Add / Edit User Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center z-50 p-4" onClick={closeModal}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full" ref={parentDropdownRef} onClick={(e) => e.stopPropagation()}>

            <div className="flex justify-between items-center px-6 py-4 border-b border-primary/10">
              <h2 className="font-roboto font-bold text-lg text-primary uppercase tracking-wide">
                {isEditing ? t.modalEditTitle : t.modalAddTitle}
              </h2>
              <button
                className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-[#F0EEF8] text-gray-400 hover:text-primary transition-colors"
                onClick={closeModal}
              >
                <IoMdClose className="text-lg" />
              </button>
            </div>

            <form onSubmit={formik.handleSubmit} className="p-6 grid gap-4">
              <div className="grid gap-1.5">
                <label className="font-roboto text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  {t.labelFullName} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  className="w-full rounded-xl px-4 py-2.5 border border-primary/20 focus:outline-none focus:ring-2 focus:ring-primary/20 bg-[#F0EEF8]/50 text-primary font-roboto text-sm"
                  placeholder={t.placeholderName}
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.name && formik.errors.name && (
                  <div className="text-red-500 text-xs">{formik.errors.name}</div>
                )}
              </div>

              {!isEditing && (
                <>
                  <div className="grid gap-1.5">
                    <label className="font-roboto text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      {t.labelEmail} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      autoComplete="off"
                      className="w-full rounded-xl px-4 py-2.5 border border-primary/20 focus:outline-none focus:ring-2 focus:ring-primary/20 bg-[#F0EEF8]/50 text-primary font-roboto text-sm"
                      placeholder={t.placeholderEmail}
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    {formik.touched.email && formik.errors.email && (
                      <div className="text-red-500 text-xs">{formik.errors.email}</div>
                    )}
                  </div>

                  <div className="grid gap-1.5">
                    <label className="font-roboto text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      {t.labelPassword} <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        autoComplete="new-password"
                        className="w-full rounded-xl px-4 py-2.5 pr-10 border border-primary/20 focus:outline-none focus:ring-2 focus:ring-primary/20 bg-[#F0EEF8]/50 text-primary font-roboto text-sm"
                        placeholder={t.placeholderPassword}
                        value={formik.values.password}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-primary transition-colors"
                        tabIndex={-1}
                      >
                        {showPassword
                          ? <AiOutlineEye className="h-5 w-5" />
                          : <AiOutlineEyeInvisible className="h-5 w-5" />
                        }
                      </button>
                    </div>
                    {formik.touched.password && formik.errors.password && (
                      <div className="text-red-500 text-xs">{formik.errors.password}</div>
                    )}
                  </div>
                </>
              )}

              <div className="grid gap-1.5" ref={dropdownRef}>
                <label className="font-roboto text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  {t.labelTeamGroup}
                </label>
                <Select
                  value={selectedGroup}
                  onChange={handleGroupSelect}
                  onInputChange={handleGroupInputChange}
                  options={groupOptions}
                  placeholder={t.placeholderGroup}
                  isSearchable
                  className="capitalize"
                  classNamePrefix="react-select"
                />
                {formik.touched.group && formik.errors.group && (
                  <div className="text-red-500 text-xs">{formik.errors.group}</div>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-primary/10 mt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-5 py-2.5 rounded-xl font-roboto font-semibold text-sm text-gray-500 border border-primary/20 hover:bg-[#F0EEF8] transition-all"
                >
                  {t.cancel}
                </button>
                <button
                  type="submit"
                  className="px-6 py-2.5 rounded-xl font-roboto font-bold text-sm text-white bg-primary hover:bg-primary/90 transition-all uppercase tracking-wide min-w-[90px]"
                >
                  {isSubmiting ? (
                    <div className="loader flex justify-center items-center mx-4" />
                  ) : (
                    isEditing ? t.update : t.submit
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Token Update Modal */}
      {/* Token Management & Usage Modal */}
      <TokenModal
        isOpen={tokenModal}
        onClose={closeTokenModal}
        user={selectedTokenUser}
        onTokenUpdated={getAllUser}
        lang={lang}
      />

      {/* Upgrade / Renew Plan Modal */}
      <UpgradePlanModal
        isOpen={planModal}
        onClose={closePlanModal}
        user={selectedPlanUser}
        onPlanUpdated={getAllUser}
        lang={lang}
      />
    </div>
  );
};

/* ── Users Table Shimmer Skeleton Rows ───────────────────────────── */
const UserTableSkeletonRows = ({ pageSize = 10 }) => {
  const rowCount = Math.min(pageSize || 10, 8);
  return (
    <>
      {Array.from({ length: rowCount }).map((_, idx) => (
        <tr key={idx} className="hover:bg-[#F0EEF8]/30">
          <td className="px-4 py-3.5">
            <div className="w-5 h-3.5 rounded skeleton-bone" />
          </td>
          <td className="px-4 py-3.5">
            <div className="w-28 h-4 rounded skeleton-bone mb-1.5" />
            <div className="w-40 h-3 rounded skeleton-bone" />
          </td>
          <td className="px-4 py-3.5">
            <div className="w-32 h-8 rounded-lg skeleton-bone" />
          </td>
          <td className="px-4 py-3.5 text-center">
            <div className="w-20 h-5 rounded-full skeleton-bone mx-auto" />
          </td>
          <td className="px-4 py-3.5">
            <div className="w-20 h-3.5 rounded skeleton-bone" />
          </td>
          <td className="px-4 py-3.5">
            <div className="w-20 h-3.5 rounded skeleton-bone" />
          </td>
          <td className="px-4 py-3.5 text-right">
            <div className="w-14 h-4 rounded skeleton-bone ml-auto" />
          </td>
          <td className="px-4 py-3.5 text-right">
            <div className="w-14 h-4 rounded skeleton-bone-primary ml-auto" />
          </td>
          <td className="px-4 py-3.5 text-center">
            <div className="w-8 h-8 rounded-lg skeleton-bone mx-auto" />
          </td>
        </tr>
      ))}
    </>
  );
};

export default UserAdmin;
